import cn from 'classnames';
import React from 'react';

import CircularProgressBar from 'components/CircularProgressBar';
import { ButtonV3 } from 'components/ComponentV2';
import Icon from 'components/Icon';

import styles from './Gamification.module.scss';

export interface GamificationProps {
  profileCompletePercent: number;
  nonCompletedSection: number;
  progressDetails?: {
    id: string;
    title: string;
    desc: string;
    isCompleted: boolean;
  };
  showMeButton?: boolean;
  handleProfileCompletionButton: () => void;
  handleNextButton?: () => void;
  handleShowMeButton?: () => void;
  showNextButton?: boolean;
  title?: string;
  description?: string;
  className?: string;
}

export const Gamification: React.FC<GamificationProps> = ({
  nonCompletedSection,
  profileCompletePercent,
  progressDetails,
  showMeButton = false,
  handleShowMeButton,
  handleProfileCompletionButton,
  handleNextButton,
  showNextButton = false,
  title,
  description,
  className = '',
}) => {
  const profileComplete = profileCompletePercent === 100;
  const sectionComplete = progressDetails?.isCompleted;

  const titleText = profileComplete
    ? 'All done!'
    : title
    ? title
    : sectionComplete
    ? 'Great!'
    : `You’re missing ${progressDetails?.title}`;

  const descText = profileComplete
    ? 'Great job, your profile is complete.'
    : description
    ? description
    : sectionComplete
    ? `Only ${nonCompletedSection} left`
    : progressDetails?.desc;

  const getButton = () => {
    if (showMeButton === true) {
      return (
        <ButtonV3
          className={styles.buttons}
          variant="contained"
          color={'primary'}
          onClick={handleShowMeButton}
        >
          Show Me
        </ButtonV3>
      );
    }
    if (showNextButton) {
      return (
        <ButtonV3
          className={styles.buttons}
          variant="contained"
          color={sectionComplete ? 'primary' : 'secondary'}
          onClick={handleNextButton}
        >
          Next
        </ButtonV3>
      );
    }

    if (profileComplete) {
      return (
        <ButtonV3
          className={styles.buttons}
          variant="contained"
          color={'secondary'}
          onClick={handleProfileCompletionButton}
        >
          Ok
        </ButtonV3>
      );
    }
  };

  return (
    <div
      className={cn(
        styles.completionScore,
        {
          [styles.complete]: profileComplete || sectionComplete,
        },
        className
      )}
    >
      <CircularProgressBar
        percentCompleted={profileCompletePercent}
        size={'medium'}
        showIcon={false}
        showChildren={false}
        showBackgroundPath={true}
        strokeWidth="5"
        className={styles['progress-circle']}
        pathClassName={styles.completionScoreProgressCircle}
      >
        <span className={styles.completionScorePercentageComplete}>
          {profileComplete ? (
            <Icon
              iconName={'icon_check'}
              size={'small'}
              className={styles.completionScorePercentageCompleteIcon}
            />
          ) : (
            `${profileCompletePercent.toFixed(0)}%`
          )}
        </span>
      </CircularProgressBar>
      <div className={styles.completionScoreContent}>
        <div className={styles.completionScoreContentTitle}>{titleText}</div>
        <div className={styles.completionScoreContentDescription}>
          {descText}
        </div>
      </div>

      {getButton()}
    </div>
  );
};

Gamification.displayName = 'Gamification';
